export const UI_ZOOM_STORAGE_KEY = 'erp-ui-zoom'
export const UI_ZOOM_MIN = 70
export const UI_ZOOM_MAX = 150
export const UI_ZOOM_STEP = 10
export const UI_ZOOM_DEFAULT = 100
export const UI_ZOOM_MIN_VIEWPORT_PX = 640

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 10

let viewportWatchInstalled = false

export function clampUiZoom(value: unknown): number {
  const parsed = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10)
  if (!Number.isFinite(parsed)) return UI_ZOOM_DEFAULT
  const snapped = Math.round(parsed / UI_ZOOM_STEP) * UI_ZOOM_STEP
  return Math.min(UI_ZOOM_MAX, Math.max(UI_ZOOM_MIN, snapped))
}

export function canApplyUiZoom(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth >= UI_ZOOM_MIN_VIEWPORT_PX
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null
  const escaped = UI_ZOOM_STORAGE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(percent: number) {
  if (typeof document === 'undefined') return
  document.cookie = `${UI_ZOOM_STORAGE_KEY}=${percent}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
}

function clearZoomStyle() {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.style.removeProperty('zoom')
  root.style.zoom = ''
  delete root.dataset.uiZoom
}

export function readUiZoom(): number {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(UI_ZOOM_STORAGE_KEY)
      if (stored != null && stored !== '') return clampUiZoom(stored)
    }
  } catch {
    /* private mode */
  }
  return clampUiZoom(readCookie())
}

export function applyUiZoom(percent: number): number {
  const next = clampUiZoom(percent)
  if (typeof document === 'undefined') return next
  if (!canApplyUiZoom() || next === UI_ZOOM_DEFAULT) {
    clearZoomStyle()
    return next
  }
  const root = document.documentElement
  root.style.zoom = String(next / 100)
  root.dataset.uiZoom = String(next)
  return next
}

export function persistUiZoom(percent: number): number {
  const next = applyUiZoom(percent)
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(UI_ZOOM_STORAGE_KEY, String(next))
    }
  } catch {
    /* private mode */
  }
  writeCookie(next)
  return next
}

export function stepUiZoom(current: number, direction: 1 | -1): number {
  return persistUiZoom(clampUiZoom(current) + direction * UI_ZOOM_STEP)
}

/** Re-apply the saved zoom after a reload, before React paints. */
export function hydrateUiZoom(): number {
  const next = applyUiZoom(readUiZoom())
  if (typeof window !== 'undefined' && !viewportWatchInstalled) {
    viewportWatchInstalled = true
    const media = window.matchMedia(`(min-width: ${UI_ZOOM_MIN_VIEWPORT_PX}px)`)
    const sync = () => applyUiZoom(readUiZoom())
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync)
    } else {
      media.addListener(sync)
    }
  }
  return next
}
